import { motion, AnimatePresence } from 'framer-motion'
import { useCV } from './useCV'
import { accentFor } from './scenes2d/theme'

export const SCENE_LABELS = [
  'How Computers See',
  'Human vs Computer Vision',
  'Images Become Numbers',
  'Images Become Tensors',
  'Convolution',
  'CNN Pipeline',
  'How Models Learn',
  'Object Detection',
  'Segmentation',
  'Vision Transformers',
  'Foundation & Self-Supervised',
  'Multimodal & VLMs',
  'Generative Vision',
  'CV Universe',
  'Learning Roadmap',
]

export function SceneHUD() {
  const scene = useCV(s => s.scene)
  const globalProgress = useCV(s => s.globalProgress)
  const accent = accentFor(scene)

  return (
    <>
      {/* Top bar */}
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '18px clamp(16px, 5vw, 32px)',
        pointerEvents: 'none',
      }}>
        {/* Left: wordmark */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{
            fontFamily: "'Clash Display', sans-serif",
            fontSize: 13, fontWeight: 700, letterSpacing: '0.04em',
            color: 'rgba(255,255,255,0.9)',
          }}>
            Computer Vision
          </span>
        </div>

        {/* Right: fraction counter */}
        <AnimatePresence mode="wait">
          <motion.div
            key={scene}
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 11, letterSpacing: '0.12em',
              color: 'rgba(255,255,255,0.22)',
              display: 'flex', alignItems: 'baseline', gap: 4,
            }}
          >
            <span style={{ fontSize: 15, color: accent, fontWeight: 500 }}>
              {String(scene + 1).padStart(2, '0')}
            </span>
            <span>/ {String(SCENE_LABELS.length).padStart(2, '0')}</span>
          </motion.div>
        </AnimatePresence>

        {/* Hairline bottom border */}
        <div style={{
          position: 'absolute', bottom: 0, left: 'clamp(16px, 5vw, 32px)', right: 'clamp(16px, 5vw, 32px)', height: 1,
          background: 'linear-gradient(90deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 60%, transparent 100%)',
        }} />
      </div>

      {/* Bottom-left chapter label block */}
      <div style={{
        position: 'fixed', bottom: 'clamp(24px, 6vw, 40px)', left: 'clamp(16px, 5vw, 40px)', right: 'clamp(16px, 5vw, 40px)', zIndex: 50,
        pointerEvents: 'none',
        display: 'flex', flexDirection: 'column', gap: 6,
      }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={scene}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Chapter tag */}
            <div style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase',
              color: accent, marginBottom: 7,
              display: 'flex', alignItems: 'center', gap: 7,
            }}>
              <div style={{
                width: 18, height: 1,
                background: accent,
                opacity: 0.7,
              }} />
              Chapter {String(scene + 1).padStart(2, '0')}
            </div>

            {/* Title */}
            <div style={{ overflow: 'hidden', padding: '0.05em 0' }}>
              <motion.div
                initial={{ y: '105%' }}
                animate={{ y: '0%' }}
                transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1], delay: 0.05 }}
                style={{
                  fontFamily: "'Clash Display', sans-serif",
                  fontSize: 'clamp(20px, 2.6vw, 30px)',
                  fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.1,
                  color: 'rgba(255,255,255,0.88)',
                  maxWidth: 340,
                }}
              >
                {SCENE_LABELS[scene]}
              </motion.div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Right-side numbered dot nav */}
      <div style={{
        position: 'fixed', right: 28, top: '50%', transform: 'translateY(-50%)',
        zIndex: 50, display: 'flex', flexDirection: 'column', gap: 6,
        pointerEvents: 'none',
      }}>
        {SCENE_LABELS.map((_, i) => {
          const isActive = i === scene
          return (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              justifyContent: 'flex-end',
            }}>
              <motion.div
                animate={{
                  width: isActive ? 20 : 0,
                  opacity: isActive ? 1 : 0,
                }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                style={{
                  height: 1,
                  background: accent,
                  overflow: 'hidden',
                }}
              />
              <div style={{
                width: isActive ? 5 : 3,
                height: isActive ? 5 : 3,
                borderRadius: '50%',
                background: isActive ? accent : 'rgba(255,255,255,0.18)',
                boxShadow: isActive ? `0 0 8px ${accent}` : 'none',
                transition: 'all 0.35s',
              }} />
            </div>
          )
        })}
      </div>

      {/* Progress bar */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 50,
        height: 1,
        background: 'rgba(255,255,255,0.04)',
        pointerEvents: 'none',
      }}>
        <motion.div
          style={{
            height: '100%',
            background: `linear-gradient(90deg, ${accent}aa, ${accent})`,
            transformOrigin: 'left',
          }}
          animate={{ scaleX: globalProgress }}
          transition={{ duration: 0.1 }}
        />
      </div>

      <style>{`
        ::-webkit-scrollbar { display: none; }
      `}</style>
    </>
  )
}
